import React, { useEffect, useMemo, useRef, useState } from "react";
import "./MicroMissionModal.css";

const INTERACTIVE_KINDS = new Set([
  "single_choice",
  "multi_choice",
  "binary",
  "scale",
  "short_text",
  "long_text",
  "complete_phrase",
  "inline_fields",
  "record_picker",
  "confirmation",
]);

function cleanText(value = "") {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .trim();
}

function makeId(prefix = "item") {
  if (window.crypto && typeof window.crypto.randomUUID === "function")
    return window.crypto.randomUUID();

  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function AutoGrowingTextarea({ value, ...props }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const maxHeight = Math.min(320, Math.max(180, window.innerHeight * 0.35));
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [value]);

  return <textarea ref={textareaRef} value={value} {...props} />;
}

function AutoGrowingInlineTextarea({ value = "", ...props }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const maxHeight = Math.min(240, Math.max(150, window.innerHeight * 0.28));
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [value]);

  return <textarea ref={textareaRef} rows={1} value={value} {...props} />;
}

function splitOptions(value = "") {
  return cleanText(value)
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((label, index) => ({
      id: `option-${index + 1}`,
      value: label,
      label,
    }));
}

function normalizeOption(option, index) {
  if (typeof option === "string") {
    return { id: `option-${index + 1}`, value: option, label: option };
  }

  const label = cleanText(option?.label || option?.text || option?.value);
  return {
    id: option?.id || `option-${index + 1}`,
    value: option?.value ?? label,
    label,
    description: cleanText(option?.description),
    route: cleanText(option?.route),
    backSteps: Number(option?.backSteps || option?.back_steps || 0),
  };
}

function normalizeBlock(block, index) {
  const id = block?.id || makeId(`block-${index + 1}`);
  const rawKind = block?.kind || "text";
  const kindMap = {
    question: "short_text",
    response: "long_text",
    binary_question: "binary",
    multi_select: "multi_choice",
    checklist: "multi_choice",
    selection: "single_choice",
    example: "single_choice",
    choice_cards: "single_choice",
  };
  const kind = kindMap[rawKind] || rawKind;
  const options = Array.isArray(block?.options)
    ? block.options.map(normalizeOption).filter((option) => option.label)
    : ["multi_choice", "single_choice"].includes(kind)
      ? splitOptions(block?.text)
      : [];

  const min = Number.isFinite(Number(block?.min)) ? Number(block.min) : 1;
  const requestedMax = Number.isFinite(Number(block?.max))
    ? Number(block.max)
    : 5;
  const max = Math.min(Math.max(requestedMax, min), min + 10);

  return {
    ...block,
    id,
    kind,
    prompt: cleanText(block?.prompt || block?.text),
    instruction: cleanText(block?.instruction || block?.placeholder),
    required:
      typeof block?.required === "boolean"
        ? block.required
        : INTERACTIVE_KINDS.has(kind),
    options,
    maxSelections: Number(block?.maxSelections || block?.max_selections || 0),
    min,
    max,
    minLabel: cleanText(block?.minLabel || block?.min_label),
    maxLabel: cleanText(block?.maxLabel || block?.max_label),
    allowOther: Boolean(block?.allowOther ?? block?.allow_other),
    allowNone: Boolean(block?.allowNone ?? block?.allow_none),
    allowUnsure: Boolean(block?.allowUnsure ?? block?.allow_unsure),
    allowSkip: Boolean(block?.allowSkip ?? block?.allow_skip),
    skipLabel: cleanText(block?.skipLabel || block?.skip_label),
    skippedLabel: cleanText(block?.skippedLabel || block?.skipped_label),
    journalLabel: cleanText(block?.journalLabel || block?.journal_label),
    routeSource: cleanText(block?.routeSource || block?.route_source),
    promptByRoute: block?.promptByRoute || block?.prompt_by_route || {},
    instructionByRoute:
      block?.instructionByRoute || block?.instruction_by_route || {},
    kindByRoute: block?.kindByRoute || block?.kind_by_route || {},
    optionsByRoute: block?.optionsByRoute || block?.options_by_route || {},
    template: cleanText(block?.template),
    templateByRoute: block?.templateByRoute || block?.template_by_route || {},
    fields: Array.isArray(block?.fields) ? block.fields : [],
    fieldsByRoute: block?.fieldsByRoute || block?.fields_by_route || {},
    maxSelectionsByRoute:
      block?.maxSelectionsByRoute || block?.max_selections_by_route || {},
    allowNoneByRoute:
      block?.allowNoneByRoute || block?.allow_none_by_route || {},
    allowSkipByRoute:
      block?.allowSkipByRoute || block?.allow_skip_by_route || {},
    answerSource: cleanText(block?.answerSource || block?.answer_source),
    secondsByAnswer: block?.secondsByAnswer || block?.seconds_by_answer || {},
  };
}

function adaptLegacyStep(step, stepIndex) {
  const sourceBlocks = Array.isArray(step?.blocks) ? step.blocks : [];
  const blocks = [];

  for (let index = 0; index < sourceBlocks.length; index += 1) {
    const block = sourceBlocks[index];

    if (block?.kind === "choice_cards") {
      const cards = [];
      let cursor = index;

      while (sourceBlocks[cursor]?.kind === "choice_cards") {
        cards.push(sourceBlocks[cursor]);
        cursor += 1;
      }

      const precedingText = blocks[blocks.length - 1];
      const canUsePrecedingText = precedingText?.kind === "text";

      blocks.push({
        id: `legacy-choice-${cards[0]?.id || index}`,
        kind: "single_choice",
        prompt: canUsePrecedingText
          ? precedingText.prompt
          : "Selecciona la opción que mejor representa tu respuesta.",
        required: true,
        options: cards.map((card, cardIndex) =>
          normalizeOption(
            {
              id: card.id,
              value: cleanText(card.text),
              label: cleanText(card.text),
            },
            cardIndex,
          ),
        ),
      });

      if (canUsePrecedingText) blocks.splice(blocks.length - 2, 1);
      index = cursor - 1;
      continue;
    }

    if (["example", "multi_select", "checklist"].includes(block?.kind)) {
      const normalized = normalizeBlock(block, index);
      const precedingText = blocks[blocks.length - 1];

      if (precedingText?.kind === "text") {
        normalized.prompt = precedingText.prompt;
        blocks.pop();
      } else {
        normalized.prompt =
          block?.placeholder ||
          (block.kind === "example"
            ? "Selecciona la opción que mejor representa tu respuesta."
            : "Selecciona las opciones que correspondan.");
      }

      blocks.push(normalized);
      continue;
    }

    const normalized = normalizeBlock(block, index);
    blocks.push(normalized);

    if (block?.kind === "text") {
      const timerMatch = cleanText(block.text).match(
        /temporizador[^\d]{0,30}(\d+)\s*(segundos?|minutos?)/i,
      );

      if (timerMatch) {
        const amount = Number(timerMatch[1]);
        const isMinutes = timerMatch[2].toLowerCase().startsWith("minuto");

        blocks.push({
          id: `${block.id || `legacy-${index}`}-timer`,
          kind: "timer",
          prompt: "Tiempo de pausa guiada",
          required: false,
          seconds: Math.min(
            1800,
            Math.max(15, isMinutes ? amount * 60 : amount),
          ),
        });
      }
    }
  }

  return {
    id: step?.id || makeId(`step-${stepIndex + 1}`),
    number: stepIndex + 1,
    title: cleanText(step?.title) || `Paso ${stepIndex + 1}`,
    description: cleanText(step?.description),
    blocks,
  };
}

function normalizeMission(missionData) {
  const source = missionData || {};
  const steps = Array.isArray(source.steps) ? source.steps : [];

  return {
    ...source,
    kind: "micro_mission",
    version: Number(source.version || 1),
    title: cleanText(source.title) || "TOMA ACCIÓN",
    intro:
      cleanText(source.intro) ||
      "Trabaja una decisión concreta a tu ritmo. Tus respuestas se guardarán en Bitácora.",
    steps: steps.map(adaptLegacyStep),
    closing: {
      title: cleanText(source.closing?.title) || "Tu recorrido de hoy",
      message:
        cleanText(source.closing?.message) ||
        "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora.",
    },
    card:
      source.card && typeof source.card === "object"
        ? {
            title: cleanText(source.card.title),
            sourceBlockId: cleanText(
              source.card.sourceBlockId || source.card.source_block_id,
            ),
            reviewBlockId: cleanText(
              source.card.reviewBlockId || source.card.review_block_id,
            ),
            saveValue: source.card.saveValue || source.card.save_value || "save",
            weekNumber: Number(
              source.card.weekNumber || source.card.week_number || 0,
            ),
            includeHistoryCards: Boolean(
              source.card.includeHistoryCards ||
                source.card.include_history_cards,
            ),
          }
        : null,
    cards: Array.isArray(source.cards)
      ? source.cards.map((card) => ({
          title: cleanText(card.title),
          sourceBlockId: cleanText(card.sourceBlockId || card.source_block_id),
          reviewBlockId: cleanText(card.reviewBlockId || card.review_block_id),
          saveValue: card.saveValue || card.save_value || "save",
          weekNumber: Number(card.weekNumber || card.week_number || 0),
          includeHistoryCards: Boolean(
            card.includeHistoryCards || card.include_history_cards,
          ),
        }))
      : [],
  };
}

function findMissionBlock(mission, blockId) {
  if (!blockId) return null;
  for (const step of mission.steps) {
    const block = step.blocks.find((candidate) => candidate.id === blockId);
    if (block) return block;
  }
  return null;
}

function responseValue(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value.value;
  }
  return value;
}

function resolveRoute(mission, block, responses) {
  if (!block.routeSource) return "";
  const sourceBlock = findMissionBlock(mission, block.routeSource);
  if (!sourceBlock) return "";
  const selected = responseValue(responses[block.routeSource]);
  if (selected === "__other__") return "custom";
  return (
    sourceBlock.options.find((option) => option.value === selected)?.route || ""
  );
}

function normalizeDynamicOptions(options = []) {
  return Array.isArray(options)
    ? options.map(normalizeOption).filter((option) => option.label)
    : [];
}

function resolveBlock(mission, block, responses) {
  const route = resolveRoute(mission, block, responses);
  const answerKey = block.answerSource
    ? responseValue(responses[block.answerSource])
    : "";
  const secondsFromAnswer = answerKey
    ? Number(block.secondsByAnswer?.[answerKey])
    : Number.NaN;
  if (!route) {
    return Number.isFinite(secondsFromAnswer)
      ? { ...block, seconds: secondsFromAnswer }
      : block;
  }

  const dynamicOptions = block.optionsByRoute?.[route];
  return {
    ...block,
    kind: block.kindByRoute?.[route] || block.kind,
    prompt: cleanText(block.promptByRoute?.[route] || block.prompt),
    instruction: cleanText(
      block.instructionByRoute?.[route] || block.instruction,
    ),
    options: dynamicOptions
      ? normalizeDynamicOptions(dynamicOptions)
      : block.options,
    template: cleanText(block.templateByRoute?.[route] || block.template),
    fields: Array.isArray(block.fieldsByRoute?.[route])
      ? block.fieldsByRoute[route]
      : block.fields,
    maxSelections: Number(
      block.maxSelectionsByRoute?.[route] ?? block.maxSelections ?? 0,
    ),
    allowNone: Boolean(
      block.allowNoneByRoute?.[route] ?? block.allowNone,
    ),
    allowSkip: Boolean(
      block.allowSkipByRoute?.[route] ?? block.allowSkip,
    ),
    seconds: Number.isFinite(secondsFromAnswer)
      ? secondsFromAnswer
      : block.seconds,
    resolvedRoute: route,
  };
}

function InlineText({ children }) {
  const value = String(children || "");
  const parts = value.split(/(\*\*.*?\*\*)/g).filter(Boolean);

  return parts.map((part, index) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={index}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    ),
  );
}

function RichText({ children }) {
  const value = cleanText(children);
  if (!value) return null;

  return value.split(/\n\s*\n/).map((paragraph, index) => (
    <p key={index}>
      {paragraph.split("\n").map((line, lineIndex) => (
        <React.Fragment key={lineIndex}>
          {lineIndex > 0 && <br />}
          <InlineText>{line}</InlineText>
        </React.Fragment>
      ))}
    </p>
  ));
}

function valueHasEvidence(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true;
  if (value && typeof value === "object") {
    return Object.values(value).some(valueHasEvidence);
  }
  return cleanText(value).length > 0;
}

function getAnswerText(block, value) {
  if (value === "__skip__") return "Preferí omitir esta respuesta por ahora.";
  if (value === "__none__") return "Ninguna de estas opciones me representa.";
  if (value === "__unsure__") return "Todavía no logro identificarlo.";

  if (block.kind === "inline_fields" && value && typeof value === "object") {
    const template = cleanText(block.template);
    if (template) {
      const completed = (block.fields || []).reduce((text, field) => {
        const answer = cleanText(value[field.id]);
        return text.split(`{${field.id}}`).join(answer || "___");
      }, template);

      if (completed && completed !== template) return completed;
    }

    return (block.fields || [])
      .map((field) => {
        const answer = cleanText(value[field.id]);
        return answer ? `${cleanText(field.label) || "Campo"}: ${answer}` : "";
      })
      .filter(Boolean)
      .join(" · ");
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === "__none__")
          return "Ninguna de estas opciones me representa.";
        if (item === "__unsure__") return "Todavía no logro identificarlo.";
        const option = block.options.find(
          (candidate) => candidate.value === item,
        );
        return option?.label || item;
      })
      .filter(Boolean)
      .join("; ");
  }

  if (value && typeof value === "object") {
    if (value.value === "__other__") {
      return value.other
        ? `Respuesta propia: ${value.other}`
        : "Elegí expresarlo con mis propias palabras.";
    }

    const parts = [];
    if (value.value) {
      const option = block.options.find(
        (candidate) => candidate.value === value.value,
      );
      parts.push(option?.label || value.value);
    }
    if (value.other) parts.push(`Respuesta propia: ${value.other}`);
    return parts.join(" · ");
  }

  if (block.kind === "binary") {
    if (value === "yes") return "Sí";
    if (value === "no") return "No";
  }

  if (block.kind === "confirmation" && value === true) {
    return "Práctica confirmada.";
  }

  const option = block.options.find((candidate) => candidate.value === value);
  return cleanText(option?.label || value);
}

function buildJournalEntries(mission, responses, missionHistory = []) {
  const entries = [];

  mission.steps.forEach((step) => {
    step.blocks.forEach((sourceBlock) => {
      const block = resolveBlock(mission, sourceBlock, responses);
      if (block.kind === "rule") {
        if (block.prompt) {
          entries.push({
            block_id: block.id,
            kind: "rule",
            question: block.journalLabel || "Práctica del día",
            answer: block.prompt,
          });
        }
        return;
      }

      if (!INTERACTIVE_KINDS.has(block.kind)) return;

      let blockForAnswer = block;
      if (block.kind === "record_picker") {
        blockForAnswer = {
          ...block,
          options: missionHistory.map((record) => ({
            id: record.id,
            value: record.id,
            label: `Día ${record.global_day_number} · Registro guardado`,
          })),
        };
      }

      const answer = getAnswerText(blockForAnswer, responses[block.id]);
      if (!answer) return;

      entries.push({
        block_id: block.id,
        kind: block.kind,
        question:
          block.journalLabel || block.prompt || "Respuesta de la actividad",
        answer,
      });
    });
  });

  return entries;
}

function buildReflectionCard(
  mission,
  responses,
  missionHistory = [],
  cardConfig = mission.card,
) {
  const config = cardConfig;
  if (!config?.title || !config?.sourceBlockId || !config?.reviewBlockId) {
    return null;
  }

  const reviewValue = responseValue(responses[config.reviewBlockId]);
  if (reviewValue !== config.saveValue) return null;

  const sourceBlock = findMissionBlock(mission, config.sourceBlockId);
  if (!sourceBlock) return null;

  const resolvedBlock = resolveBlock(mission, sourceBlock, responses);
  const body = getAnswerText(
    resolvedBlock,
    responses[config.sourceBlockId],
  );
  if (!body) return null;

  const historyBodies = config.includeHistoryCards
    ? missionHistory
        .map((record) => record?.reflection_card)
        .filter((card) => card?.title && card?.body)
        .map((card) => `${card.title}: ${card.body}`)
    : [];

  return {
    schema_version: 1,
    title: config.title,
    body: [...historyBodies, body].join("\n\n"),
    week_number: config.weekNumber || null,
  };
}

function buildReflectionCards(mission, responses, missionHistory = []) {
  const configs = mission.cards.length
    ? mission.cards
    : mission.card
      ? [mission.card]
      : [];
  return configs
    .map((config) =>
      buildReflectionCard(mission, responses, missionHistory, config),
    )
    .filter(Boolean);
}

function InlineFields({ block, value, onChange }) {
  const fields = Array.isArray(block.fields) ? block.fields : [];
  const current = value && typeof value === "object" ? value : {};
  const template = block.template || "";
  const fieldMap = new Map(fields.map((field) => [field.id, field]));
  const parts = template.split(/(\{[a-zA-Z0-9_-]+\})/g).filter(Boolean);

  function updateField(fieldId, fieldValue) {
    onChange({ ...current, [fieldId]: fieldValue });
  }

  return (
    <div className="missionInlineSentence">
      {parts.map((part, index) => {
        const match = part.match(/^\{([a-zA-Z0-9_-]+)\}$/);
        if (!match) return <span key={`${part}-${index}`}>{part}</span>;
        const field = fieldMap.get(match[1]);
        if (!field) return null;
        const fieldValue = String(current[field.id] || "");
        const placeholder = field.placeholder || field.label || "Escribe aquí";
        const preferredChars = Math.min(
          42,
          Math.max(20, fieldValue.length + 2, placeholder.length + 2),
        );
        const configuredMaxLength = Number(field.maxLength);
        return (
          <label
            key={field.id}
            className="missionInlineField"
            style={{ "--mission-inline-width": `${preferredChars}ch` }}
          >
            <span className="sr-only">{field.label || "Completar campo"}</span>
            <AutoGrowingInlineTextarea
              maxLength={
                Number.isFinite(configuredMaxLength) && configuredMaxLength > 0
                  ? configuredMaxLength
                  : undefined
              }
              value={fieldValue}
              onChange={(event) => updateField(field.id, event.target.value)}
              placeholder={placeholder}
              aria-label={field.label || "Completar campo"}
            />
          </label>
        );
      })}
    </div>
  );
}

function RecordPicker({ block, value, onChange, missionHistory = [] }) {
  const selected = Array.isArray(value) ? value : [];
  const fromDay = Number(block.fromDay || block.from_day || 1);
  const toDay = Number(block.toDay || block.to_day || 28);
  const maxSelections = Number(block.maxSelections || 0);
  const records = missionHistory.filter((record) => {
    const day = Number(record.global_day_number || 0);
    return day >= fromDay && day <= toDay;
  });

  function toggle(recordId) {
    const exists = selected.includes(recordId);
    const next = exists
      ? selected.filter((id) => id !== recordId)
      : [...selected.filter((id) => id !== "__none__"), recordId];
    if (maxSelections > 0 && next.length > maxSelections) return;
    onChange(next);
  }

  return (
    <div className="missionRecordPicker">
      {records.map((record) => {
        const checked = selected.includes(record.id);
        return (
          <button
            key={record.id}
            type="button"
            className={`missionChoice ${checked ? "is-selected" : ""}`}
            onClick={() => toggle(record.id)}
            aria-pressed={checked}
          >
            <span className="missionChoiceControl" aria-hidden="true" />
            <span>
              <strong>Día {record.global_day_number}</strong>
              <small>{record.summary || "Registro guardado"}</small>
            </span>
          </button>
        );
      })}
      {block.allowNone && (
        <button
          type="button"
          className={`missionChoice ${selected.includes("__none__") ? "is-selected" : ""}`}
          onClick={() =>
            onChange(selected.includes("__none__") ? [] : ["__none__"])
          }
          aria-pressed={selected.includes("__none__")}
        >
          <span className="missionChoiceControl" aria-hidden="true" />
          <span>
            <strong>Todavía no tengo evidencia suficiente</strong>
          </span>
        </button>
      )}
    </div>
  );
}

function ChoiceOptions({ block, value, onChange, multiple = false }) {
  const selected = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value?.value || value;
  const otherValue =
    !multiple && value && typeof value === "object" ? value.other || "" : "";
  const options = [...block.options];

  if (block.allowNone) {
    options.push({
      id: "none",
      value: "__none__",
      label: "Ninguna de estas opciones me representa.",
    });
  }
  if (block.allowUnsure) {
    options.push({
      id: "unsure",
      value: "__unsure__",
      label: "Todavía no logro identificarlo.",
    });
  }

  function toggle(optionValue) {
    if (!multiple) {
      onChange(
        block.allowOther ? { value: optionValue, other: "" } : optionValue,
      );
      return;
    }

    if (["__none__", "__unsure__"].includes(optionValue)) {
      onChange(selected.includes(optionValue) ? [] : [optionValue]);
      return;
    }

    const withoutExclusive = selected.filter(
      (item) => !["__none__", "__unsure__"].includes(item),
    );
    const exists = withoutExclusive.includes(optionValue);
    const next = exists
      ? withoutExclusive.filter((item) => item !== optionValue)
      : [...withoutExclusive, optionValue];

    if (block.maxSelections > 0 && next.length > block.maxSelections) return;
    onChange(next);
  }

  return (
    <div className="missionChoiceList">
      {options.map((option) => {
        const checked = multiple
          ? selected.includes(option.value)
          : selected === option.value;

        return (
          <button
            key={option.id || option.value}
            type="button"
            className={`missionChoice ${checked ? "is-selected" : ""}`}
            onClick={() => toggle(option.value)}
            aria-pressed={checked}
          >
            <span className="missionChoiceControl" aria-hidden="true" />
            <span>
              <strong>
                <InlineText>{option.label}</InlineText>
              </strong>
              {option.description && (
                <small>
                  <InlineText>{option.description}</InlineText>
                </small>
              )}
            </span>
          </button>
        );
      })}

      {!multiple && block.allowOther && (
        <div
          className={`missionOther ${selected === "__other__" ? "is-selected" : ""}`}
        >
          <button
            type="button"
            className={`missionChoice missionChoice--other ${
              selected === "__other__" ? "is-selected" : ""
            }`}
            onClick={() => onChange({ value: "__other__", other: otherValue })}
            aria-pressed={selected === "__other__"}
          >
            <span className="missionChoiceControl" aria-hidden="true" />
            <span>
              <strong>Usar mis propias palabras</strong>
            </span>
          </button>
          {selected === "__other__" && (
            <AutoGrowingTextarea
              rows={3}
              maxLength={500}
              value={otherValue}
              onChange={(event) =>
                onChange({ value: "__other__", other: event.target.value })
              }
              placeholder="Escribe una respuesta breve (opcional)."
              autoFocus
            />
          )}
        </div>
      )}
    </div>
  );
}

function MissionBlock({ block, value, onChange, missionHistory }) {
  if (["text", "info"].includes(block.kind)) {
    return (
      <div className="missionEditorial">
        <RichText>{block.prompt}</RichText>
      </div>
    );
  }

  if (block.kind === "rule") {
    return (
      <div className="missionRule">
        <span>Práctica del día</span>
        <RichText>{block.prompt}</RichText>
      </div>
    );
  }

  if (block.kind === "timer") {
    return <MissionTimer block={block} />;
  }

  return (
    <fieldset className="missionFieldset">
      <legend>
        <RichText>{block.prompt}</RichText>
      </legend>
      {block.instruction && (
        <p className="missionInstruction">{block.instruction}</p>
      )}
      {!block.required && <span className="missionOptional">Opcional</span>}

      {block.kind === "single_choice" && (
        <ChoiceOptions block={block} value={value} onChange={onChange} />
      )}

      {block.kind === "multi_choice" && (
        <>
          <ChoiceOptions
            block={block}
            value={value}
            onChange={onChange}
            multiple
          />
          {block.maxSelections > 0 && (
            <p className="missionLimit">
              Puedes elegir hasta {block.maxSelections}.
            </p>
          )}
        </>
      )}

      {block.kind === "binary" && (
        <div className="missionBinary">
          {[
            { value: "yes", label: "Sí" },
            { value: "no", label: "No" },
            ...(block.allowUnsure
              ? [
                  {
                    value: "__unsure__",
                    label: "Todavía no logro identificarlo",
                  },
                ]
              : []),
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              className={value === option.value ? "is-selected" : ""}
              onClick={() => onChange(option.value)}
              aria-pressed={value === option.value}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {block.kind === "scale" && (
        <div className="missionScale">
          <div className="missionScaleLabels">
            <span>{block.minLabel || `Nivel ${block.min}`}</span>
            <span>{block.maxLabel || `Nivel ${block.max}`}</span>
          </div>
          <div className="missionScaleButtons">
            {Array.from(
              { length: block.max - block.min + 1 },
              (_, index) => block.min + index,
            ).map((number) => (
              <button
                key={number}
                type="button"
                className={Number(value) === number ? "is-selected" : ""}
                onClick={() => onChange(number)}
                aria-pressed={Number(value) === number}
              >
                {number}
              </button>
            ))}
          </div>
        </div>
      )}

      {["short_text", "complete_phrase"].includes(block.kind) && (
        <input
          type="text"
          maxLength={500}
          value={typeof value === "string" && value !== "__skip__" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={block.instruction || "Escribe una respuesta breve."}
        />
      )}

      {block.kind === "long_text" && (
        <AutoGrowingTextarea
          rows={4}
          maxLength={1200}
          value={typeof value === "string" && value !== "__skip__" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={block.instruction || "Escribe con tus palabras."}
        />
      )}

      {block.kind === "inline_fields" && (
        <InlineFields block={block} value={value} onChange={onChange} />
      )}

      {block.kind === "record_picker" && (
        <RecordPicker
          block={block}
          value={value}
          onChange={onChange}
          missionHistory={missionHistory}
        />
      )}

      {block.kind === "confirmation" && (
        <label className="missionConfirmation">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span>
            {block.confirmationLabel || "Deseo intentar esta práctica."}
          </span>
        </label>
      )}

      {block.allowSkip && (
        <button
          type="button"
          className={`missionSkip ${value === "__skip__" ? "is-selected" : ""}`}
          onClick={() => onChange(value === "__skip__" ? "" : "__skip__")}
        >
          {value === "__skip__"
            ? block.skippedLabel || "Omitido por ahora"
            : block.skipLabel || "Omitir por ahora"}
        </button>
      )}
    </fieldset>
  );
}

function MissionTimer({ block }) {
  const configuredSeconds = Number(
    block?.seconds ?? block?.durationSeconds ?? 60,
  );
  const initialSeconds = configuredSeconds <= 0 ? 0 : Math.max(15, configuredSeconds);
  const [seconds, setSeconds] = useState(initialSeconds);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running || seconds <= 0) return undefined;
    const timer = window.setInterval(
      () => setSeconds((value) => value - 1),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [running, seconds]);

  useEffect(() => {
    if (seconds === 0) setRunning(false);
  }, [seconds]);

  useEffect(() => {
    setRunning(false);
    setSeconds(initialSeconds);
  }, [initialSeconds]);

  if (initialSeconds <= 0) return null;

  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;

  return (
    <div className="missionTimer">
      <div>
        <RichText>{block.prompt}</RichText>
      </div>
      <strong>
        {String(minutes).padStart(2, "0")}:{String(remainder).padStart(2, "0")}
      </strong>
      <div>
        <button type="button" onClick={() => setRunning((value) => !value)}>
          {running ? "Pausar" : seconds === 0 ? "Terminado" : "Iniciar"}
        </button>
        <button
          type="button"
          onClick={() => {
            setRunning(false);
            setSeconds(initialSeconds);
          }}
        >
          Reiniciar
        </button>
      </div>
    </div>
  );
}

export default function MicroMissionModal({
  open,
  onClose,
  missionData,
  missionProgress,
  missionHistory = [],
  onComplete,
}) {
  const mission = useMemo(() => normalizeMission(missionData), [missionData]);
  const [stepIndex, setStepIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [validationError, setValidationError] = useState("");
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(Boolean(missionProgress?.id));
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const savingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const missionProgressRef = useRef(missionProgress);

  onCloseRef.current = onClose;
  missionProgressRef.current = missionProgress;

  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  useEffect(() => {
    if (!open || !bodyRef.current) return;
    bodyRef.current.scrollTop = 0;
  }, [open, stepIndex, completed]);

  useEffect(() => {
    if (!open) return undefined;

    setStepIndex(0);
    setResponses({});
    setValidationError("");
    setCompleted(Boolean(missionProgressRef.current?.id));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => dialogRef.current?.focus());

    function handleKeyDown(event) {
      if (event.key === "Escape" && !savingRef.current) onCloseRef.current?.();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!open) return null;

  const totalSteps = mission.steps.length;
  const isClosing = completed;
  const currentStep = mission.steps[stepIndex];
  const currentBlocks = currentStep
    ? currentStep.blocks.map((block) => resolveBlock(mission, block, responses))
    : [];
  const progress = isClosing
    ? 100
    : totalSteps > 0
      ? Math.round(((stepIndex + 1) / totalSteps) * 100)
      : 0;

  function setBlockResponse(blockId, value) {
    setResponses((current) => {
      const next = { ...current, [blockId]: value };
      const pending = [blockId];
      while (pending.length > 0) {
        const sourceId = pending.shift();
        mission.steps.forEach((step) => {
          step.blocks.forEach((candidate) => {
            if (
              candidate.routeSource === sourceId &&
              candidate.id !== blockId
            ) {
              delete next[candidate.id];
              pending.push(candidate.id);
            }
          });
        });
      }
      return next;
    });
    setValidationError("");
  }

  function currentStepIsValid() {
    if (!currentStep) return true;

    return currentBlocks.every((block) => {
      if (!INTERACTIVE_KINDS.has(block.kind) || !block.required) return true;
      if (block.allowSkip && responses[block.id] === "__skip__") return true;

      if (block.kind === "confirmation") {
        return responses[block.id] === true;
      }

      if (block.kind === "inline_fields") {
        const value = responses[block.id];
        if (!value || typeof value !== "object") return false;
        return (block.fields || []).every(
          (field) =>
            field.required === false || cleanText(value[field.id]).length > 0,
        );
      }

      const value = responses[block.id];
      if (block.allowOther && value?.value === "__other__") {
        return block.required ? cleanText(value.other).length > 0 : true;
      }
      return valueHasEvidence(value);
    });
  }

  async function goForward() {
    if (!currentStepIsValid()) {
      setValidationError(
        "Completa la respuesta necesaria o utiliza una alternativa disponible para continuar.",
      );
      return;
    }

    const revisionOption = currentBlocks
      .filter((block) => block.kind === "single_choice")
      .map((block) => {
        const selected = responseValue(responses[block.id]);
        return block.options.find(
          (option) => option.value === selected && option.backSteps > 0,
        );
      })
      .find(Boolean);

    if (revisionOption) {
      setStepIndex((value) => Math.max(0, value - revisionOption.backSteps));
      return;
    }

    if (stepIndex < totalSteps - 1) {
      setStepIndex((value) => value + 1);
      return;
    }

    try {
      setSaving(true);
      setValidationError("");

      const journalEntries = buildJournalEntries(
        mission,
        responses,
        missionHistory,
      );
      const reflectionCards = buildReflectionCards(
        mission,
        responses,
        missionHistory,
      );
      const reflectionCard =
        reflectionCards[reflectionCards.length - 1] || null;
      await onComplete?.({
        schema_version: 3,
        responses,
        journal_entries: journalEntries,
        reflection_card: reflectionCard,
        reflection_cards: reflectionCards,
        completed_step_ids: mission.steps.map((step) => step.id),
        client_completed_at: new Date().toISOString(),
      });

      setCompleted(true);
    } catch (error) {
      console.error("[MISSION_SAVE_ERROR]", error);
      setValidationError(
        "No se pudo guardar la actividad. Intenta nuevamente.",
      );
    } finally {
      setSaving(false);
    }
  }

  const journalEntries = buildJournalEntries(
    mission,
    responses,
    missionHistory,
  );

  return (
    <div className="missionOverlay" role="presentation">
      <button
        type="button"
        className="missionBackdrop"
        onClick={saving ? undefined : onClose}
        aria-label="Cerrar actividad"
      />

      <section
        className="missionShell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mission-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="missionHeader">
          <div>
            <span className="missionEyebrow">Experiencia práctica</span>
            <h2 id="mission-title">{mission.title}</h2>
            {!isClosing && <p>{mission.intro}</p>}
          </div>
          <button
            type="button"
            className="missionClose"
            onClick={onClose}
            disabled={saving}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        {!isClosing && totalSteps > 0 && (
          <div
            className="missionProgress"
            aria-label={`Paso ${stepIndex + 1} de ${totalSteps}`}
          >
            <div>
              <span>
                Paso {stepIndex + 1} de {totalSteps}
              </span>
              <strong>{progress}%</strong>
            </div>
            <div className="missionProgressTrack">
              <span style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <div className="missionBody" ref={bodyRef}>
          {isClosing ? (
            <div className="missionClosing" role="status">
              <div className="missionClosingIcon" aria-hidden="true">
                ✓
              </div>
              <span>Actividad guardada</span>
              <h3>
                {mission.version >= 2
                  ? mission.closing.title
                  : "Tu recorrido de hoy"}
              </h3>
              <p>
                {mission.version >= 2
                  ? mission.closing.message
                  : "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora."}
              </p>

              {journalEntries.length > 0 && (
                <div className="missionSummary">
                  <h4>Lo que trabajaste</h4>
                  {journalEntries.slice(0, 4).map((entry) => (
                    <div key={entry.block_id}>
                      <span>{entry.question}</span>
                      <strong>{entry.answer}</strong>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="missionPrimary"
                onClick={onClose}
              >
                Volver al día
              </button>
            </div>
          ) : currentStep ? (
            <article className="missionStep">
              <div className="missionStepHeading">
                <span>Paso {stepIndex + 1}</span>
                {!new RegExp(`^Paso\\s+${stepIndex + 1}$`, "i").test(
                  cleanText(currentStep.title),
                ) && <h3>{currentStep.title}</h3>}
                {currentStep.description && <p>{currentStep.description}</p>}
              </div>

              <div className="missionBlocks">
                {currentBlocks.map((block) => (
                  <MissionBlock
                    key={block.id}
                    block={block}
                    value={responses[block.id]}
                    onChange={(value) => setBlockResponse(block.id, value)}
                    missionHistory={missionHistory}
                  />
                ))}
              </div>
            </article>
          ) : (
            <div className="missionEmpty">
              <h3>Actividad no configurada</h3>
              <p>Este bloque todavía no contiene pasos disponibles.</p>
            </div>
          )}
        </div>

        {!isClosing && currentStep && (
          <footer className="missionFooter">
            <button
              type="button"
              className="missionSecondary"
              onClick={() => {
                setValidationError("");
                setStepIndex((value) => Math.max(0, value - 1));
              }}
              disabled={stepIndex === 0 || saving}
            >
              Atrás
            </button>

            <div>
              {validationError && (
                <p className="missionError" role="alert">
                  {validationError}
                </p>
              )}
              <button
                type="button"
                className="missionPrimary"
                onClick={goForward}
                disabled={saving}
              >
                {saving
                  ? "Guardando..."
                  : stepIndex === totalSteps - 1
                    ? "Guardar en Bitácora"
                    : "Continuar"}
              </button>
            </div>
          </footer>
        )}
      </section>
    </div>
  );
}
